import { AppScope, IContactInformation, IDashboardFilters, ITransactionLogFilters } from "../types";
import { BasePlatformRootStore } from "../base/BasePlatformRootStore";
import { BaseStore } from "../base/BaseStore";
import { ClientsApi, PartnersApi, SuppliersApi } from "../apis";
import { Transaction } from "../models";
import { UnhandledScopeError } from "../base";
import { action, autorun, computed, observable, runInAction } from "mobx";

export class TransactionStore extends BaseStore {
    protected readonly scope: AppScope;
    @observable public transactions: Transaction[] = [];
    @observable public managersContactInformation: IContactInformation;

    constructor(rootStore: BasePlatformRootStore) {
        super(rootStore);
        this.scope = rootStore.scope;
    }

    @action
    getManagersContactInformation = async () => {
        await this.tryCatch(async () => {
            let contactInformation: IContactInformation;

            switch (this.scope) {
                case AppScope.Client:
                    if (this.rootStore.userInfoStore.clientId) {
                        contactInformation = await ClientsApi.getManagerContactInfo(
                            this.rootStore.userInfoStore.clientId,
                        );
                    }
                    break;
                case AppScope.Partner:
                    if (this.rootStore.userInfoStore.partnerId) {
                        contactInformation = await PartnersApi.getManagerContactInfo(
                            this.rootStore.userInfoStore.partnerId,
                        );
                    }
                    break;
            }

            runInAction(() => {
                this.managersContactInformation = contactInformation;
            });
        });
    };

    @action
    searchTransactionLog = async (
        filters?: ITransactionLogFilters,
        fetchSize: number = 100,
        offset: number = 0,
        sortDirection: "asc" | "desc" = "desc",
    ) => {
        let transactions: Transaction[] = [];

        await this.tryCatch(async () => {
            switch (this.scope) {
                case AppScope.Client:
                    if (this.rootStore.userInfoStore.clientId) {
                        transactions = await ClientsApi.getClientTransactions(
                            this.rootStore.userInfoStore.clientId,
                            filters,
                            fetchSize,
                            offset,
                            sortDirection,
                        );
                    }
                    break;
                case AppScope.Partner:
                    if (this.rootStore.userInfoStore.partnerId) {
                        transactions = await PartnersApi.getPartnerTransactions(
                            this.rootStore.userInfoStore.partnerId,
                            filters,
                            fetchSize,
                            offset,
                            sortDirection,
                        );
                    }
                    break;
                case AppScope.Supplier:
                    if (this.rootStore.userInfoStore.supplierId) {
                        transactions = await SuppliersApi.getSupplierTransactions(
                            this.rootStore.userInfoStore.supplierId,
                            filters,
                            fetchSize,
                            offset,
                            sortDirection,
                        );
                    }
                    break;
            }
        });

        return transactions;
    };

    @action
    downloadTransactionLog = async (filters?: ITransactionLogFilters) => {
        switch (this.scope) {
            case AppScope.Client:
                if (this.rootStore.userInfoStore.clientId) {
                    await ClientsApi.getClientTransactionsExcel(this.rootStore.userInfoStore.clientId, filters);
                }
                break;
            case AppScope.Partner:
                if (this.rootStore.userInfoStore.partnerId) {
                    await PartnersApi.getPartnerTransactionsExcel(this.rootStore.userInfoStore.partnerId, filters);
                }
                break;
            case AppScope.Supplier:
                if (this.rootStore.userInfoStore.supplierId) {
                    await SuppliersApi.getSupplierTransactionsExcel(this.rootStore.userInfoStore.supplierId, filters);
                }
                break;
        }
    };

    @action
    loadTransactionLog = async () => {
        await this.tryCatch(async () => {
            let transactions: Transaction[];

            switch (this.scope) {
                case AppScope.Client:
                    if (this.rootStore.userInfoStore.clientId) {
                        transactions = await ClientsApi.getClientTransactions(this.rootStore.userInfoStore.clientId);
                    }
                    break;
                case AppScope.Partner:
                    if (this.rootStore.userInfoStore.partnerId) {
                        transactions = await PartnersApi.getPartnerTransactions(this.rootStore.userInfoStore.partnerId);
                    }
                    break;
                case AppScope.Supplier:
                    if (this.rootStore.userInfoStore.supplierId) {
                        transactions = await SuppliersApi.getSupplierTransactions(
                            this.rootStore.userInfoStore.supplierId,
                        );
                    }
                    break;
            }

            runInAction(() => {
                this.transactions = transactions;
                this.isLoading = false;
            });
        });
    };

    @action
    getDashboardElements = async (filters?: IDashboardFilters) => {
        return await this.tryCatch(async () => {
            switch (this.scope) {
                case AppScope.Partner:
                case AppScope.Supplier:
                    return await PartnersApi.getDashboardElements(filters);
                default:
                    throw new UnhandledScopeError(this.scope);
            }
        });
    };

    @action
    getUserById = (id: string) => {
        return this.transactions.find((x) => x.id === id);
    };

    @action
    getUserIndexById = (id: string) => {
        return this.transactions.findIndex((x) => x.id === id);
    };

    @computed
    get transactionsCount() {
        return this.transactions.length;
    }

    autorunner = autorun(() => {});
}
